import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { faCrown } from "@fortawesome/free-solid-svg-icons/faCrown";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { FontSize } from "../../../../components/Text";
import { AtiraSpacePlanType } from "../../../../model/plan/types/AtiraSpacePlanType.enum";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { WHATSAPP_META_RECONNECT_PARAM } from "../../../integration/utils/integration-utils";
import { useDrawersContext } from "../../../tasks/drawers-context";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";
import { DashboardSetupPlanBadge } from "./DashboardSetupPlanBadge";

const WhatsappCard = styled(SetupCard)`
  background-color: ${({ theme }) => `${theme.whatsapp}1f`};
  box-shadow: none;
`;

const ChatWindow = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${Rounded.lg};
  border: 1px solid ${({ theme }) => `${theme.whatsapp}66`};
  background-color: ${({ theme }) => `${theme.white}99`};
  overflow: hidden;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.xs} ${Spacing.s};
  border-bottom: 1px solid ${({ theme }) => `${theme.whatsapp}66`};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.lightBlack};
  font-size: ${FontSize.s};
  font-weight: 500;
`;

const ChatAvatar = styled.span`
  display: grid;
  place-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.whatsapp};
  color: ${({ theme }) => theme.white};
`;

const Bubbles = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.s};
`;

const Message = styled.div<{ $mine?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  align-self: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  gap: 2px;
  max-width: 90%;
`;

const MessageRow = styled.div<{ $mine?: boolean }>`
  display: flex;
  flex-direction: ${({ $mine }) => ($mine ? "row" : "row-reverse")};
  align-items: flex-end;
  gap: ${Spacing.xs};
`;

const MessageAvatar = styled.span<{ $mine?: boolean }>`
  display: grid;
  place-items: center;
  flex: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme, $mine }) => ($mine ? theme.main : theme.sub)};
  color: ${({ theme, $mine }) => ($mine ? theme.white : theme.darkerSub)};
`;

const MessageCaption = styled.span`
  padding-inline: calc(1.5rem + ${Spacing.xs});
  font-size: ${FontSize.xs};
  color: ${({ theme }) => theme.darkerSub};
`;

const Bubble = styled.span<{ $mine?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.lg};
  border-end-start-radius: ${({ $mine }) => ($mine ? Rounded.lg : Rounded.sm)};
  border-end-end-radius: ${({ $mine }) => ($mine ? Rounded.sm : Rounded.lg)};
  background-color: ${({ theme, $mine }) =>
    $mine ? `${theme.whatsapp}4d` : theme.white};
  border: 1px solid ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.lightBlack};
  font-size: ${FontSize.s};
`;

const IncludedBadge = styled.span`
  align-self: flex-start;
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.sm};
  background-color: ${({ theme }) => theme.whatsapp};
  color: ${({ theme }) => theme.lightBlack};
  font-size: ${FontSize.xs};
  font-weight: bold;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

type DashboardSetupWhatsappCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupWhatsappCard: React.FC<
  DashboardSetupWhatsappCardProps
> = ({ featured, done }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSubToUnlock } = useDrawersContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const planType = user?.plan?.type;
  const onPaidPlan =
    Boolean(planType) && planType !== AtiraSpacePlanType.PLAN_0;

  const onConnect = () => {
    if (done) {
      navigate("/integration/whatsapp-meta");
      return;
    }

    if (!onPaidPlan) {
      setSubToUnlock({
        visible: true,
        title: t("subToUnlock.specific.whatsappMetaPlanLimit"),
      });
      return;
    }

    navigate(`/integration?${WHATSAPP_META_RECONNECT_PARAM}=1`);
  };

  return (
    <WhatsappCard
      $featured={featured}
      $done={done}
      data-testid={TestIDs.dashboard.setup.card("WHATSAPP")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      {done ? null : onPaidPlan ? (
        <IncludedBadge>{t("dashboard.setup.whatsapp.included")}</IncludedBadge>
      ) : (
        <DashboardSetupPlanBadge />
      )}
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.whatsapp.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.whatsapp.description")}
      </SetupCardDescription>

      <ChatWindow aria-hidden>
        <ChatHeader>
          <ChatAvatar>
            <AtiraIcon icon={faWhatsapp} size="sm" />
          </ChatAvatar>
          {t("dashboard.setup.whatsapp.sample_contact")}
        </ChatHeader>
        <Bubbles>
          <Message>
            <MessageRow>
              <Bubble>{t("dashboard.setup.whatsapp.sample_in")}</Bubble>
              <MessageAvatar>
                <AtiraIcon icon={faUser} size="sm" />
              </MessageAvatar>
            </MessageRow>
            <MessageCaption>
              {t("dashboard.setup.whatsapp.customer_caption")}
            </MessageCaption>
          </Message>
          <Message $mine>
            <MessageRow $mine>
              <Bubble $mine>{t("dashboard.setup.whatsapp.sample_out")}</Bubble>
              <MessageAvatar $mine>
                <AtiraIcon icon={faRobot} size="sm" />
              </MessageAvatar>
            </MessageRow>
            <MessageCaption>
              {t("dashboard.setup.whatsapp.ai_caption")}
            </MessageCaption>
          </Message>
        </Bubbles>
      </ChatWindow>

      <SetupCardActions $center>
        <Button
          icon={done || onPaidPlan ? faWhatsapp : faCrown}
          onClick={onConnect}
          dataTestId={TestIDs.dashboard.setup.cta("WHATSAPP")}
        >
          {done
            ? t("dashboard.setup.whatsapp.open_inbox")
            : onPaidPlan
              ? t("dashboard.setup.whatsapp.cta")
              : t("dashboard.setup.see_plans")}
        </Button>
      </SetupCardActions>
    </WhatsappCard>
  );
};
